import {
  useDataProvider,
  useGetList,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

import type { CrmDataProvider } from "../providers/types";
import type { AppRole, Deal, PropertyOption } from "../types";

const SELECT_CLASS =
  "h-11 w-full rounded-md border bg-background px-3 text-base disabled:opacity-60";

/**
 * Almikia Epic EC (TASK-032): "Vender esta opción". Creates the corretaje
 * operation of a DISPONIBLE option from a buyer lead the viewer may sell for
 * (an asesora / lider her own leads; direccion and lead_manager any lead in
 * their reach) and opens it. The option becomes apartada; the server function
 * re-checks the role, the lead and the option before writing anything.
 */
export const SellOptionDialog = ({
  option,
  role,
  userId,
}: {
  option: PropertyOption;
  role: AppRole;
  userId: Identifier | undefined;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const navigate = useNavigate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [open, setOpen] = useState(false);
  const [leadId, setLeadId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ownLeadsOnly = role === "asesora" || role === "lider";
  const { data: leads, isPending } = useGetList<Deal>(
    "deals",
    {
      filter: ownLeadsOnly ? { sales_id: userId } : {},
      pagination: { page: 1, perPage: 200 },
      sort: { field: "name", order: "ASC" },
    },
    { enabled: open },
  );

  const confirm = async () => {
    const lead = (leads ?? []).find((row) => String(row.id) === leadId);
    if (!lead) return;
    setBusy(true);
    setError(null);
    try {
      const operationId = await dataProvider.createCorretajeOperation(
        option.id,
        lead.id,
      );
      notify("crm.corretaje.sell.created", { type: "success" });
      setOpen(false);
      navigate(`/operations/${operationId}/show`);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : translate("crm.operations.error"),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="gradient"
        className="min-h-11"
        onClick={() => {
          setError(null);
          setLeadId("");
          setOpen(true);
        }}
      >
        {translate("crm.corretaje.sell.action")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{translate("crm.corretaje.sell.title")}</DialogTitle>
            <DialogDescription>
              {translate("crm.corretaje.sell.description", {
                address: option.address,
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="sell-lead">
              {translate("crm.corretaje.sell.lead")}
            </Label>
            <select
              id="sell-lead"
              className={SELECT_CLASS}
              value={leadId}
              disabled={isPending}
              onChange={(e) => setLeadId(e.target.value)}
            >
              <option value="">{translate("crm.corretaje.sell.choose")}</option>
              {(leads ?? []).map((lead) => (
                <option key={lead.id} value={String(lead.id)}>
                  {lead.name}
                </option>
              ))}
            </select>
            {!isPending && (leads ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {translate("crm.corretaje.sell.no_leads")}
              </p>
            ) : null}
            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              onClick={() => setOpen(false)}
            >
              {translate("crm.operations.confirm.back")}
            </Button>
            <Button
              type="button"
              className="min-h-11"
              disabled={busy || leadId === ""}
              onClick={confirm}
            >
              {translate("crm.corretaje.sell.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
